import React, { useState, useEffect, useCallback } from 'react';
import { Activity, X } from 'lucide-react';
import { Modal, ModalContent } from '../shared/Modal';
import { useTheme } from '../../contexts/ThemeContext';
import { FearGreedData, fetchFearGreedIndex, getSentimentColor, getSentimentBg } from '../../services/fearGreedService';

interface FearGreedWidgetProps {
  variant?: 'card' | 'page';
}

export const FearGreedWidget: React.FC<FearGreedWidgetProps> = () => {
  const { darkMode } = useTheme();
  const [data, setData] = useState<FearGreedData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchFearGreedIndex(30);
      setData(result);
    } catch {
      setError('Unable to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const current = data[0];
  const yesterday = data[1];
  const weekAgo = data[6];
  const monthAgo = data[29];

  const muted = darkMode ? 'text-slate-500' : 'text-slate-400';

  const renderGauge = (value: number, size: number, labelSize = 9, valueSize = 22) => {
    const radius = size / 2 - 8;
    const circumference = Math.PI * radius;
    const filled = (value / 100) * circumference;
    const strokeWidth = Math.max(6, size * 0.05);

    return (
      <svg width={size} height={size / 2 + 24} viewBox={`0 0 ${size} ${size / 2 + 24}`}>
        <path
          d={`M ${strokeWidth / 2 + 4} ${size / 2 + 4} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2 - 4} ${size / 2 + 4}`}
          fill="none"
          stroke={darkMode ? '#ffffff10' : '#00000010'}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        <path
          d={`M ${strokeWidth / 2 + 4} ${size / 2 + 4} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2 - 4} ${size / 2 + 4}`}
          fill="none"
          stroke={
            value <= 25 ? '#ef4444' :
            value <= 45 ? '#f97316' :
            value <= 55 ? '#eab308' :
            value <= 75 ? '#22c55e' : '#10b981'
          }
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${filled} ${circumference}`}
        />
        <text x={size / 2} y={size / 2 - 2} textAnchor="middle" fill={darkMode ? '#fff' : '#111'} fontSize={valueSize} fontWeight="bold">
          {value}
        </text>
        <text x={size / 2} y={size / 2 + 14} textAnchor="middle" fill={darkMode ? '#9ca3af' : '#6b7280'} fontSize={labelSize}>
          {current?.classification}
        </text>
      </svg>
    );
  };

  const renderHistoryRow = (label: string, entry: FearGreedData | undefined) => {
    if (!entry) return null;
    return (
      <div className="flex items-center justify-between py-1.5">
        <span className={`text-xs ${muted}`}>{label}</span>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-medium ${getSentimentColor(entry.value, darkMode)}`}>
            {entry.value} — {entry.classification}
          </span>
          <div className={`w-8 h-1.5 rounded-full ${darkMode ? 'bg-white/10' : 'bg-black/5'} overflow-hidden`}>
            <div
              className={`h-full rounded-full ${getSentimentBg(entry.value, darkMode)}`}
              style={{ width: `${entry.value}%` }}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Compact clickable card */}
      <button
        onClick={() => !loading && !error && setShowModal(true)}
        className={`text-left rounded-xl p-5 transition-all hover:scale-[1.02] flex flex-col justify-between h-full ${
          darkMode
            ? 'bg-white/5 hover:bg-white/10 border border-white/10'
            : 'bg-white/80 hover:bg-white/95 border border-slate-200 shadow-sm hover:shadow-md'
        }`}
      >
        <div className="flex items-center gap-2 mb-2">
          <Activity size={16} className={darkMode ? 'text-violet-400' : 'text-violet-600'} />
          <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Crypto Fear & Greed</h3>
        </div>

        {loading ? (
          <div className={`text-center py-4 text-xs ${muted}`}>Loading...</div>
        ) : error ? (
          <div className={`text-center py-4 text-xs ${muted}`}>{error}</div>
        ) : current ? (
          <div className="flex-1 flex flex-col">
            <div className="flex-1 flex items-center justify-center">
              {renderGauge(current.value, 140, 8, 20)}
            </div>
            <p className={`text-[8px] text-right ${muted}`}>
              <a
                href="https://alternative.me/crypto/fear-and-greed-index/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
                onClick={(e) => e.stopPropagation()}
              >
                alternative.me
              </a>
            </p>
          </div>
        ) : null}
      </button>

      {/* Detail modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} maxWidth="sm">
        <ModalContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity size={20} className={darkMode ? 'text-violet-400' : 'text-violet-600'} />
                <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Crypto Fear & Greed</h3>
              </div>
              <button onClick={() => setShowModal(false)} className={`p-1 rounded-lg ${darkMode ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}>
                <X size={18} className={muted} />
              </button>
            </div>

            {current && (
              <>
                <div className="flex justify-center">
                  {renderGauge(current.value, 200, 11, 28)}
                </div>

                <div className={`border-t ${darkMode ? 'border-white/10' : 'border-slate-200'} pt-3 space-y-0`}>
                  {renderHistoryRow('Yesterday', yesterday)}
                  {renderHistoryRow('Last Week', weekAgo)}
                  {renderHistoryRow('Last Month', monthAgo)}
                </div>

                {data.length > 2 && (
                  <div>
                    <p className={`text-xs ${muted} mb-1`}>30-Day Trend</p>
                    <svg viewBox="0 0 200 40" className="w-full h-10">
                      <rect x="0" y="0" width="200" height="10" fill={darkMode ? '#ef444415' : '#ef444410'} />
                      <rect x="0" y="10" width="200" height="8" fill={darkMode ? '#f9731615' : '#f9731610'} />
                      <rect x="0" y="18" width="200" height="4" fill={darkMode ? '#eab30815' : '#eab30810'} />
                      <rect x="0" y="22" width="200" height="8" fill={darkMode ? '#22c55e15' : '#22c55e10'} />
                      <rect x="0" y="30" width="200" height="10" fill={darkMode ? '#10b98115' : '#10b98110'} />
                      <polyline
                        fill="none"
                        stroke={darkMode ? '#a78bfa' : '#7c3aed'}
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                        points={[...data].reverse().map((d, i) => {
                          const x = (i / (data.length - 1)) * 200;
                          const y = 40 - (d.value / 100) * 40;
                          return `${x.toFixed(1)},${y.toFixed(1)}`;
                        }).join(' ')}
                      />
                    </svg>
                    <div className="flex justify-between">
                      <span className={`text-[9px] ${muted}`}>30d ago</span>
                      <span className={`text-[9px] ${muted}`}>Today</span>
                    </div>
                  </div>
                )}

                <p className={`text-[9px] text-right ${muted}`}>
                  Data by{' '}
                  <a
                    href="https://alternative.me/crypto/fear-and-greed-index/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    alternative.me
                  </a>
                </p>
              </>
            )}
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};
